import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import ImageUploader from '@/admin/ImageUploader';
import { TextField, Toggle, Section, EyebrowSizeField } from '@/admin/AdminUI';
import { Plus, Trash2, ChevronUp, ChevronDown, UploadCloud, X, CheckCircle2, AlertCircle, Info } from 'lucide-react';

// ================================================================
// v3.13 — Bulk Upload Logos (frontend-only convenience layer)
// ----------------------------------------------------------------
// This block adds multi-file logo intake ON TOP of the existing
// single-file upload pipeline. It does NOT touch the backend,
// ImageUploader, PartnerIn schema, or any other admin control. The
// bulk flow calls POST /api/admin/uploads once per file in a
// sequential loop (never unbounded parallel), then calls
// POST /api/admin/partners for each successful upload.
// ================================================================

// Mirrors the backend allow-list in server.py (ALLOWED_EXT).
const BULK_ALLOWED_EXT = ['.jpg', '.jpeg', '.png', '.webp', '.gif', '.svg'];
// Mirrors backend MAX_UPLOAD_BYTES so we fail fast client-side.
const BULK_MAX_BYTES = 10 * 1024 * 1024; // 10 MB

// Turn "athletes_abroad-summit.png" into a readable "Athletes Abroad
// Summit". Strips extension, replaces separators with spaces, collapses
// whitespace, trims, and title-cases each word — but preserves any
// existing ALL-CAPS acronyms (2–5 chars) like "ESPN", "NBA", "USA".
function deriveNameFromFilename(fname) {
  if (typeof fname !== 'string') return '';
  const withoutExt = fname.replace(/\.[^./\\]+$/, '');
  const normalized = withoutExt.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
  if (!normalized) return '';
  return normalized
    .split(' ')
    .map((word) => {
      if (
        word.length >= 2 &&
        word.length <= 5 &&
        word === word.toUpperCase() &&
        /[A-Z]/.test(word)
      ) {
        return word; // preserve acronym
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

// Canonical form used for dedupe (against existing partners AND within
// the same batch). Case-insensitive; whitespace collapsed.
function normalizeForDedupe(s) {
  return String(s || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function getFileExt(fname) {
  const m = /(\.[^.]+)$/.exec(fname || '');
  return m ? m[1].toLowerCase() : '';
}

// Discover the trusted R2 host purely from OBSERVED state — the most
// common host across existing partner logo URLs. This lets us verify
// that a newly-returned upload URL points at the same host that the
// current 27+ partners already live on, without exposing R2 secrets
// or adding a new public config field. Returns null if state has no
// R2-shaped URLs yet (first-ever partner in an empty DB).
function inferR2Host(items) {
  const counts = {};
  for (const p of items || []) {
    const u = typeof p?.logo === 'string' ? p.logo.trim() : '';
    if (/^https?:\/\//i.test(u)) {
      try {
        const host = new URL(u).host;
        counts[host] = (counts[host] || 0) + 1;
      } catch (_) { /* ignore malformed */ }
    }
  }
  const entries = Object.entries(counts);
  if (entries.length === 0) return null;
  entries.sort((a, b) => b[1] - a[1]);
  return entries[0][0];
}

// A URL is considered R2-persisted when: it's an absolute HTTPS URL AND
// (when observable) matches the host used by existing partners' logos.
// Local fallback URLs like "/api/uploads/…" always fail this check and
// therefore never lead to a partner-record write.
function isValidR2Url(url, expectedHost) {
  if (typeof url !== 'string') return false;
  const trimmed = url.trim();
  if (!/^https:\/\//i.test(trimmed)) return false;
  try {
    const parsed = new URL(trimmed);
    if (expectedHost && parsed.host !== expectedHost) return false;
    return true;
  } catch (_) {
    return false;
  }
}

export default function PartnersTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sectionMeta, setSectionMeta] = useState({});
  const [savedMeta, setSavedMeta] = useState(false);

  // v3.13 — Bulk upload state (fully independent of the single-item
  // `editing` modal state so the two flows never interfere).
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkMode, setBulkMode] = useState('select');
  // ^ 'select' | 'preview' | 'running' | 'summary'
  const [bulkAnalysis, setBulkAnalysis] = useState(null);
  const [bulkProgress, setBulkProgress] = useState({ done: 0, total: 0, current: '' });
  const [bulkResult, setBulkResult] = useState({ added: [], skipped: [], failed: [] });
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    const r = await api.get('/admin/partners');
    setItems(r.data || []);
    const s = await api.get('/admin/settings');
    setSectionMeta((s.data?.sections_meta || {}).sponsors || {});
  };
  useEffect(() => { load(); }, []);

  const addNew = () => setEditing({ name: '', logo: '', url: '', visible: true, order: items.length });
  const remove = async (id) => {
    if (!window.confirm('Delete this partner?')) return;
    await api.delete(`/admin/partners/${id}`);
    load();
  };
  const move = async (id, dir) => {
    const idx = items.findIndex((i) => i.id === id);
    const swap = idx + dir;
    if (swap < 0 || swap >= items.length) return;
    const next = [...items];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setItems(next);
    await api.put('/admin/partners/reorder', { items: next.map((it, i) => ({ id: it.id, order: i })) });
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = { name: editing.name, logo: editing.logo || '', url: editing.url || '', visible: editing.visible !== false, order: typeof editing.order === 'number' ? editing.order : items.length };
      if (editing.id) await api.put(`/admin/partners/${editing.id}`, payload);
      else await api.post('/admin/partners', payload);
      setEditing(null); load();
    } finally { setSaving(false); }
  };

  const setMeta = (k, v) => setSectionMeta({ ...sectionMeta, [k]: v });

  // v3.15 — Per-eyebrow size commit handlers. The sponsors tab drives
  // TWO independent eyebrows (Partners left + Gallery right) so we
  // expose two dedicated commit functions. n === null removes the key
  // entirely so the sections-meta payload omits it and the site falls
  // back to the 26px default. See EyebrowSizeField for full contract.
  const commitPartnersEyebrowSize = (n) => {
    if (n === null) {
      const next = { ...sectionMeta };
      delete next.partners_eyebrow_size;
      setSectionMeta(next);
    } else {
      setSectionMeta({ ...sectionMeta, partners_eyebrow_size: n });
    }
  };
  const commitGalleryEyebrowSize = (n) => {
    if (n === null) {
      const next = { ...sectionMeta };
      delete next.gallery_eyebrow_size;
      setSectionMeta(next);
    } else {
      setSectionMeta({ ...sectionMeta, gallery_eyebrow_size: n });
    }
  };
  const saveMeta = async () => {
    const all = await api.get('/admin/settings');
    const next = { ...(all.data?.sections_meta || {}), sponsors: { ...sectionMeta } };
    await api.put('/admin/settings/sections-meta', next);
    setSavedMeta(true); setTimeout(() => setSavedMeta(false), 2000);
  };

  // ============================================================
  // v3.13 — Bulk upload flow
  // ============================================================
  const openBulk = () => {
    if (bulkBusy) return; // guard: cannot re-open mid-run
    setBulkMode('select');
    setBulkAnalysis(null);
    setBulkProgress({ done: 0, total: 0, current: '' });
    setBulkResult({ added: [], skipped: [], failed: [] });
    setBulkOpen(true);
  };

  const closeBulk = async () => {
    if (bulkBusy) return; // guard: cannot close mid-batch
    setBulkOpen(false);
    setBulkMode('select');
    setBulkAnalysis(null);
    setBulkProgress({ done: 0, total: 0, current: '' });
    setBulkResult({ added: [], skipped: [], failed: [] });
    // Refresh so the newly-added partners appear in the standard grid.
    await load();
  };

  // Phase 0 — pre-batch validation. NO network calls; partitions the
  // picked files into queue vs. three skip buckets so the operator can
  // confirm (or cancel) before any upload fires.
  const analyzeFiles = (fileList) => {
    const files = Array.from(fileList || []);
    const existingNames = new Set(items.map((p) => normalizeForDedupe(p.name)));

    const queue = [];
    const skipExisting = [];
    const skipDupInBatch = [];
    const skipInvalid = [];
    const seenInBatch = new Set();

    for (const file of files) {
      const ext = getFileExt(file.name);
      const derivedName = deriveNameFromFilename(file.name);
      const normalized = normalizeForDedupe(derivedName);
      const displayName = derivedName || file.name;

      if (!BULK_ALLOWED_EXT.includes(ext)) {
        skipInvalid.push({
          name: displayName,
          reason: `Unsupported extension "${ext || '(none)'}"`,
        });
        continue;
      }
      if (file.size > BULK_MAX_BYTES) {
        skipInvalid.push({
          name: displayName,
          reason: `Exceeds 10 MB (${(file.size / 1024 / 1024).toFixed(1)} MB)`,
        });
        continue;
      }
      if (!normalized) {
        skipInvalid.push({
          name: file.name,
          reason: 'Cannot derive a partner name from filename',
        });
        continue;
      }
      if (existingNames.has(normalized)) {
        skipExisting.push({
          name: derivedName,
          reason: `A partner named "${derivedName}" already exists`,
        });
        continue;
      }
      if (seenInBatch.has(normalized)) {
        skipDupInBatch.push({
          name: derivedName,
          reason: 'Duplicate name earlier in this selection',
        });
        continue;
      }
      seenInBatch.add(normalized);
      queue.push({ file, name: derivedName });
    }

    setBulkAnalysis({
      queue,
      skipExisting,
      skipDupInBatch,
      skipInvalid,
      totalPicked: files.length,
    });
    setBulkMode('preview');
  };

  // Phase 1 — sequential upload + create loop. Each file gets its own
  // POST /admin/uploads; on a valid R2 URL, a POST /admin/partners
  // creates the record with a contiguous order derived from the LIVE
  // max at batch start plus the successful-create counter. Failures on
  // any single file do not abort the batch.
  const runBulk = async () => {
    if (bulkBusy || !bulkAnalysis) return;
    const queue = bulkAnalysis.queue;

    const skippedFromPhase0 = [
      ...bulkAnalysis.skipExisting.map((s) => ({ name: s.name, reason: s.reason })),
      ...bulkAnalysis.skipDupInBatch.map((s) => ({ name: s.name, reason: s.reason })),
      ...bulkAnalysis.skipInvalid.map((s) => ({ name: s.name, reason: s.reason })),
    ];

    if (queue.length === 0) {
      setBulkResult({ added: [], skipped: skippedFromPhase0, failed: [] });
      setBulkMode('summary');
      return;
    }

    setBulkBusy(true);
    setBulkMode('running');
    setBulkProgress({ done: 0, total: queue.length, current: queue[0]?.name || '' });

    const nextOrderBase =
      Math.max(-1, ...items.map((p) => Number(p.order) || 0)) + 1;
    const expectedHost = inferR2Host(items);
    let successfulCreateCount = 0;
    const added = [];
    const failed = [];

    for (let i = 0; i < queue.length; i++) {
      const { file, name } = queue[i];
      setBulkProgress({ done: i, total: queue.length, current: name });

      let uploadResp;
      try {
        const form = new FormData();
        form.append('file', file);
        uploadResp = await api.post('/admin/uploads', form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } catch (uploadErr) {
        failed.push({
          name,
          reason:
            uploadErr?.response?.data?.detail ||
            uploadErr?.message ||
            'Upload failed',
        });
        continue;
      }

      const url = uploadResp?.data?.url;
      if (!isValidR2Url(url, expectedHost)) {
        // Local fallback path or unexpected host → refuse to create a
        // partner record pointing at ephemeral media.
        failed.push({
          name,
          reason: 'R2 persistence not confirmed — logo not saved',
        });
        continue;
      }

      try {
        await api.post('/admin/partners', {
          name,
          logo: url,
          url: '',
          visible: true,
          order: nextOrderBase + successfulCreateCount,
        });
        added.push({
          name,
          logo: url,
          order: nextOrderBase + successfulCreateCount,
        });
        successfulCreateCount += 1;
      } catch (createErr) {
        failed.push({
          name,
          reason:
            createErr?.response?.data?.detail ||
            createErr?.message ||
            'Partner create failed',
        });
      }
    }

    setBulkProgress({ done: queue.length, total: queue.length, current: '' });
    setBulkResult({ added, skipped: skippedFromPhase0, failed });
    setBulkMode('summary');
    setBulkBusy(false);
  };

  return (
    <div>
      <Section title="Section Labels (Partners + Gallery)">
        <div className="font-display font-bold uppercase text-[12px] text-ink-muted mb-1">Partners (left side)</div>
        <TextField label="Partners Eyebrow" value={sectionMeta.partners_eyebrow} onChange={(v) => setMeta('partners_eyebrow', v)} />
        <TextField label="Partners Headline" value={sectionMeta.partners_headline} onChange={(v) => setMeta('partners_headline', v)} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Partners Button Label" value={sectionMeta.partners_button_label} onChange={(v) => setMeta('partners_button_label', v)} />
          <TextField label="Partners Button URL" value={sectionMeta.partners_button_url} onChange={(v) => setMeta('partners_button_url', v)} />
        </div>
        <EyebrowSizeField
          label="Partners Eyebrow Size (px)"
          value={sectionMeta.partners_eyebrow_size}
          onCommit={commitPartnersEyebrowSize}
          testid="partners-eyebrow-size"
          hint="Controls only the orange PARTNERS eyebrow (left column). Independent of the Gallery eyebrow size below."
        />
        <div className="font-display font-bold uppercase text-[12px] text-ink-muted mb-1 mt-4">Gallery (right side)</div>
        <TextField label="Gallery Eyebrow" value={sectionMeta.gallery_eyebrow} onChange={(v) => setMeta('gallery_eyebrow', v)} />
        <TextField label="Gallery Headline" value={sectionMeta.gallery_headline} onChange={(v) => setMeta('gallery_headline', v)} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Gallery Button Label" value={sectionMeta.gallery_button_label} onChange={(v) => setMeta('gallery_button_label', v)} />
          <TextField label="Gallery Button URL" value={sectionMeta.gallery_button_url} onChange={(v) => setMeta('gallery_button_url', v)} />
        </div>
        <EyebrowSizeField
          label="Gallery Eyebrow Size (px)"
          value={sectionMeta.gallery_eyebrow_size}
          onCommit={commitGalleryEyebrowSize}
          testid="gallery-eyebrow-size"
          hint="Controls only the orange GALLERY eyebrow (right column). Independent of the Partners eyebrow size above."
        />
        <div className="mt-3 flex items-center gap-3">
          <button onClick={saveMeta} className="btn-primary text-[12px] !py-2 !px-4" data-testid="save-sponsors-meta">Save Section Labels</button>
          {savedMeta && <span className="font-body text-[12px] text-green-700">Saved ✓</span>}
        </div>
      </Section>

      <Section title="Partners" action={
        <div className="flex items-center gap-2">
          <button
            onClick={openBulk}
            disabled={bulkBusy}
            className="btn-secondary text-[12px] !py-2.5 !px-4 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="bulk-upload-logos"
          >
            <UploadCloud size={14} className="mr-1" /> {bulkBusy ? 'Uploading…' : 'Bulk Upload Logos'}
          </button>
          <button onClick={addNew} className="btn-primary text-[12px] !py-2.5 !px-4" data-testid="add-partner"><Plus size={14} className="mr-1" /> Add Partner</button>
        </div>
      }>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((it, idx) => (
            <div key={it.id} className="flex items-center gap-3 p-3 border border-border-line rounded-sm bg-white">
              <div className="w-20 h-14 border border-border-line bg-bg-canvas overflow-hidden flex items-center justify-center shrink-0">
                {it.logo ? <img src={it.logo} alt={it.name} className="max-h-12 max-w-full object-contain" /> : <span className="font-display text-[10px] uppercase text-ink-soft">No logo</span>}
              </div>
              <div className="flex-1">
                <div className="font-display font-bold uppercase text-ink-primary text-[14px] tracking-tightish">{it.name}</div>
                <div className="font-body text-[11px] text-ink-soft">{it.url || 'No URL'}</div>
              </div>
              <div className="flex flex-col gap-1">
                <button onClick={() => move(it.id, -1)} disabled={idx === 0} className="p-1 disabled:opacity-30"><ChevronUp size={14} /></button>
                <button onClick={() => move(it.id, 1)} disabled={idx === items.length - 1} className="p-1 disabled:opacity-30"><ChevronDown size={14} /></button>
              </div>
              <button onClick={() => setEditing({ ...it })} className="btn-secondary !py-2 !px-3 text-[11px]">Edit</button>
              <button onClick={() => remove(it.id)} className="btn-secondary !py-2 !px-3 text-[11px]"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </Section>

      {editing && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4" onClick={() => setEditing(null)}>
          <div className="bg-bg-elevated max-w-xl w-full rounded-lg p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold uppercase text-[18px] mb-4">{editing.id ? 'Edit Partner' : 'New Partner'}</h3>
            <ImageUploader value={editing.logo} onChange={(v) => setEditing({ ...editing, logo: v })} label="Logo" />
            <div className="mt-4 space-y-3">
              <TextField label="Partner Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} />
              <TextField label="URL (optional)" value={editing.url} onChange={(v) => setEditing({ ...editing, url: v })} />
              <Toggle label="Visible" value={editing.visible !== false} onChange={(v) => setEditing({ ...editing, visible: v })} />
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="btn-secondary text-[12px] !py-2 !px-4">Cancel</button>
              <button onClick={save} disabled={saving} className="btn-primary text-[12px] !py-2 !px-4">{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* v3.13 — Bulk Upload Logos modal. Fully independent of the
          single-partner `editing` modal above. When busy, clicks on
          the backdrop and the close button are inert so the batch
          cannot be interrupted. */}
      {bulkOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center px-4"
          onClick={closeBulk}
          data-testid="bulk-modal"
        >
          <div
            className="bg-bg-elevated max-w-2xl w-full rounded-lg p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold uppercase text-[18px]" data-testid="bulk-modal-title">
                Bulk Upload Logos
              </h3>
              <button
                onClick={closeBulk}
                disabled={bulkBusy}
                className="btn-secondary !py-1.5 !px-2 text-[11px] disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="bulk-close"
                aria-label="Close bulk upload"
              >
                <X size={14} />
              </button>
            </div>

            {bulkMode === 'select' && (
              <div data-testid="bulk-select">
                <p className="font-body text-[13px] text-ink-primary/80 mb-3">
                  Select multiple image files. Each becomes a new partner. Filenames are used as the partner name — you can rename any partner afterwards via <span className="font-semibold">Edit</span>.
                </p>
                <label
                  className="block cursor-pointer border-2 border-dashed border-border-line rounded-md p-8 text-center hover:bg-bg-canvas transition-colors"
                  data-testid="bulk-drop-zone"
                >
                  <UploadCloud size={28} className="mx-auto text-ink-soft mb-2" />
                  <div className="font-body text-[13px] text-ink-primary">Choose logo files</div>
                  <div className="font-body text-[11px] text-ink-soft mt-1">
                    JPG, PNG, WEBP, GIF, SVG · max 10 MB each
                  </div>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => analyzeFiles(e.target.files)}
                    data-testid="bulk-file-input"
                  />
                </label>
              </div>
            )}

            {bulkMode === 'preview' && bulkAnalysis && (
              <div data-testid="bulk-preview">
                <div className="border border-border-line rounded-sm bg-white p-4 mb-4">
                  <div className="font-display font-bold text-[13px] uppercase text-ink-primary mb-2">Ready to upload</div>
                  <div className="font-body text-[13px] text-ink-primary" data-testid="bulk-preview-summary">
                    {bulkAnalysis.queue.length} of {bulkAnalysis.totalPicked} file{bulkAnalysis.totalPicked === 1 ? '' : 's'} will be uploaded.
                  </div>
                  {(bulkAnalysis.skipExisting.length + bulkAnalysis.skipDupInBatch.length + bulkAnalysis.skipInvalid.length) > 0 && (
                    <div className="font-body text-[12px] text-ink-soft mt-1">
                      Skipped: {bulkAnalysis.skipExisting.length} existing, {bulkAnalysis.skipDupInBatch.length} in-selection duplicates, {bulkAnalysis.skipInvalid.length} invalid type/size.
                    </div>
                  )}
                </div>

                {bulkAnalysis.queue.length > 0 && (
                  <div className="mb-3">
                    <div className="font-display font-bold text-[11px] uppercase text-ink-muted mb-1">To upload</div>
                    <ul className="max-h-36 overflow-y-auto border border-border-line rounded-sm bg-bg-canvas p-2 text-[12px] font-body" data-testid="bulk-queue-list">
                      {bulkAnalysis.queue.map((q, i) => (
                        <li key={`q-${i}`} className="py-0.5 flex items-center gap-2">
                          <CheckCircle2 size={12} className="text-green-600 shrink-0" /> {q.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {(bulkAnalysis.skipExisting.length + bulkAnalysis.skipDupInBatch.length + bulkAnalysis.skipInvalid.length) > 0 && (
                  <div className="mb-3">
                    <div className="font-display font-bold text-[11px] uppercase text-ink-muted mb-1">Will be skipped</div>
                    <ul className="max-h-32 overflow-y-auto border border-border-line rounded-sm bg-bg-canvas p-2 text-[12px] font-body" data-testid="bulk-skip-list">
                      {[...bulkAnalysis.skipExisting, ...bulkAnalysis.skipDupInBatch, ...bulkAnalysis.skipInvalid].map((s, i) => (
                        <li key={`s-${i}`} className="py-0.5 flex items-start gap-2">
                          <Info size={12} className="text-ink-soft shrink-0 mt-0.5" />
                          <span><span className="font-semibold">{s.name}</span> — <span className="text-ink-soft">{s.reason}</span></span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={() => setBulkMode('select')}
                    className="btn-secondary text-[12px] !py-2 !px-4"
                    data-testid="bulk-back"
                  >
                    Back
                  </button>
                  <button
                    onClick={runBulk}
                    disabled={bulkAnalysis.queue.length === 0}
                    className="btn-primary text-[12px] !py-2 !px-4 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="bulk-confirm"
                  >
                    Upload {bulkAnalysis.queue.length} logo{bulkAnalysis.queue.length === 1 ? '' : 's'}
                  </button>
                </div>
              </div>
            )}

            {bulkMode === 'running' && (
              <div data-testid="bulk-running">
                <div className="font-body text-[13px] text-ink-primary mb-2" data-testid="bulk-progress-text">
                  Uploading… {bulkProgress.done} of {bulkProgress.total}
                </div>
                {bulkProgress.current && (
                  <div className="font-body text-[12px] text-ink-soft mb-3 truncate">Current: {bulkProgress.current}</div>
                )}
                <div className="w-full h-2 bg-bg-canvas rounded-full overflow-hidden">
                  <div
                    className="h-full bg-brand-orange"
                    style={{
                      width: `${bulkProgress.total > 0 ? (bulkProgress.done / bulkProgress.total) * 100 : 0}%`,
                      transitionProperty: 'width',
                      transitionDuration: '200ms',
                    }}
                  />
                </div>
                <p className="font-body text-[11px] text-ink-soft mt-3">
                  Do not close this window until the batch finishes.
                </p>
              </div>
            )}

            {bulkMode === 'summary' && (
              <div data-testid="bulk-summary">
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="border border-border-line bg-white rounded-sm p-3 text-center">
                    <div className="font-display font-bold text-[24px] text-green-700" data-testid="bulk-added-count">{bulkResult.added.length}</div>
                    <div className="font-body text-[10px] uppercase text-ink-soft tracking-wider2">Added</div>
                  </div>
                  <div className="border border-border-line bg-white rounded-sm p-3 text-center">
                    <div className="font-display font-bold text-[24px] text-ink-soft" data-testid="bulk-skipped-count">{bulkResult.skipped.length}</div>
                    <div className="font-body text-[10px] uppercase text-ink-soft tracking-wider2">Skipped</div>
                  </div>
                  <div className="border border-border-line bg-white rounded-sm p-3 text-center">
                    <div className="font-display font-bold text-[24px] text-brand-red" data-testid="bulk-failed-count">{bulkResult.failed.length}</div>
                    <div className="font-body text-[10px] uppercase text-ink-soft tracking-wider2">Failed</div>
                  </div>
                </div>

                {bulkResult.added.length > 0 && (
                  <div className="mb-3">
                    <div className="font-display font-bold text-[11px] uppercase text-ink-muted mb-1">Added</div>
                    <ul className="max-h-28 overflow-y-auto border border-border-line rounded-sm bg-bg-canvas p-2 text-[12px] font-body" data-testid="bulk-added-list">
                      {bulkResult.added.map((a, i) => (
                        <li key={`a-${i}`} className="py-0.5 flex items-center gap-2">
                          <CheckCircle2 size={12} className="text-green-600 shrink-0" /> {a.name}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {bulkResult.skipped.length > 0 && (
                  <div className="mb-3">
                    <div className="font-display font-bold text-[11px] uppercase text-ink-muted mb-1">Skipped</div>
                    <ul className="max-h-28 overflow-y-auto border border-border-line rounded-sm bg-bg-canvas p-2 text-[12px] font-body" data-testid="bulk-summary-skipped">
                      {bulkResult.skipped.map((s, i) => (
                        <li key={`sk-${i}`} className="py-0.5 flex items-start gap-2">
                          <Info size={12} className="text-ink-soft shrink-0 mt-0.5" />
                          <span><span className="font-semibold">{s.name}</span> — <span className="text-ink-soft">{s.reason}</span></span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {bulkResult.failed.length > 0 && (
                  <div className="mb-3">
                    <div className="font-display font-bold text-[11px] uppercase text-ink-muted mb-1">Failed</div>
                    <ul className="max-h-28 overflow-y-auto border border-border-line rounded-sm bg-bg-canvas p-2 text-[12px] font-body" data-testid="bulk-summary-failed">
                      {bulkResult.failed.map((f, i) => (
                        <li key={`f-${i}`} className="py-0.5 flex items-start gap-2">
                          <AlertCircle size={12} className="text-brand-red shrink-0 mt-0.5" />
                          <span><span className="font-semibold">{f.name}</span> — <span className="text-ink-soft">{f.reason}</span></span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex justify-end gap-2 mt-4">
                  <button
                    onClick={closeBulk}
                    className="btn-primary text-[12px] !py-2 !px-4"
                    data-testid="bulk-summary-close"
                  >
                    Done
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
